/**
 * Shell version check + update — the "brain". The SW (public/push-sw.js) just
 * executes cache commands; this file owns every update decision and loop guard.
 *
 * Two paths (app-shell-instant-open.md, Simplify Pass 1):
 *  - Cold boot (initShellUpdate, from index.js): fetch version.json, honor the
 *    kill-switch, then precache the CURRENT shell in the background so the next
 *    open is instant AND current. Never reloads — a cold-boot reload can't win
 *    the race against the already-painted cached shell.
 *  - Warm reopen (startVersionCheck → visibilitychange): if the server is newer,
 *    precache the new shell, THEN reload onto it. Because the SW serves
 *    cache-first, the reload must land on a freshly-cached shell — otherwise it
 *    just re-serves the stale one. Loop-safe (see maybeReloadForUpdate).
 */

import { getVersionNumber } from '../version';
import { logEvent } from './analytics/eventLogger';

const RELOADED_VERSION_KEY = 'shell_reloaded_for_version';
const PRECACHE_TIMEOUT_MS = 2500;

let initialized = false;

/** Fetch { version, kill } from the server, or null on any failure. */
async function fetchVersionInfo() {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`);
    if (!res.ok) return null;
    const json = await res.json();
    if (typeof json?.version !== 'number') return null;
    return { version: json.version, kill: json.kill === true };
  } catch {
    return null;
  }
}

/**
 * The controlling SW, or null if there's no registration (e.g. dev/localhost).
 * Never awaits navigator.serviceWorker.ready — with no registration that hangs
 * forever. On the first load after install, waits briefly for the SW to claim.
 */
async function getController() {
  if (!('serviceWorker' in navigator)) return null;
  if (navigator.serviceWorker.controller) return navigator.serviceWorker.controller;

  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return null; // no SW here (dev) — bail without hanging
  if (navigator.serviceWorker.controller) return navigator.serviceWorker.controller;

  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(navigator.serviceWorker.controller), 1500);
    navigator.serviceWorker.addEventListener(
      'controllerchange',
      () => {
        clearTimeout(timer);
        resolve(navigator.serviceWorker.controller);
      },
      { once: true }
    );
  });
}

/** The shell URLs to cache: current index.html + hashed main bundle (from the manifest). */
async function getShellUrls() {
  try {
    const res = await fetch(`/asset-manifest.json?t=${Date.now()}`);
    if (!res.ok) return null;
    const manifest = await res.json();
    const files = manifest?.files || {};
    const urls = [files['index.html'], files['main.js'], files['main.css']].filter(Boolean);
    return urls.length ? urls : null;
  } catch {
    return null;
  }
}

/**
 * Ask the SW to precache the current shell atomically (index.html + bundles).
 * Resolves true on the SW's confirmation; false on no controller / no manifest /
 * timeout / error.
 */
async function precacheShell(controller) {
  const sw = controller || (await getController());
  if (!sw) return false;
  const urls = await getShellUrls();
  if (!urls) return false;

  return new Promise((resolve) => {
    const channel = new MessageChannel();
    const timer = setTimeout(() => resolve(false), PRECACHE_TIMEOUT_MS);
    channel.port1.onmessage = (e) => {
      clearTimeout(timer);
      resolve(!!(e.data && e.data.ok));
    };
    sw.postMessage({ type: 'PRECACHE_SHELL', urls }, [channel.port2]);
  });
}

/** Fire the kill-switch: the SW drops its caches and unregisters (next load is SW-free). */
async function killShell() {
  const controller = await getController();
  if (controller) controller.postMessage({ type: 'KILL_SWITCH' });
}

/**
 * Cold boot (from index.js). Honor the kill-switch, then precache the current
 * shell in the background so the next open is instant + current. Never reloads.
 */
export async function initShellUpdate() {
  const info = await fetchVersionInfo();
  if (!info) return;
  if (info.kill) {
    await killShell();
    return;
  }
  // Precache the current shell every cold boot: populates the cache on the first
  // open and keeps it current. Cheap — no-store index.html + immutable bundles.
  await precacheShell();
}

/** Warm reopen. If the server is newer, precache the new shell then reload onto it. */
async function maybeReloadForUpdate() {
  const info = await fetchVersionInfo();
  if (!info) return;
  if (info.kill) {
    await killShell();
    return;
  }

  const bundled = getVersionNumber();
  if (info.version <= bundled) return;

  // Never reload mid-exchange (canonical signal: window.__EXCHANGE_DATA__, set by
  // App.js when the prefetch resolves — NOT sessionStorage.user_type; see auth.md).
  if (window.__EXCHANGE_DATA__) return;

  // Reload-once-per-target-version: if we already reloaded for this version and
  // still see it, stop and run cached (a later deploy sets a new key, so it can
  // still update). Keyed to the version, not a raw session counter.
  if (sessionStorage.getItem(RELOADED_VERSION_KEY) === String(info.version)) {
    logEvent('shell_update_loop_blocked', { version: info.version, bundled });
    return;
  }

  // Cache-before-reload: the SW serves cache-first, so the new shell must be in
  // cache BEFORE we reload, or the reload just re-serves the stale shell.
  const controller = await getController();
  if (controller) {
    const cached = await precacheShell(controller);
    if (!cached) {
      logEvent('shell_update_precache_timeout', { version: info.version, bundled });
      return; // run cached, self-heal on the next reopen
    }
  }
  // No controller → dev/localhost with no SW; index.html is no-cache, so a plain
  // reload fetches fresh HTML directly.

  sessionStorage.setItem(RELOADED_VERSION_KEY, String(info.version));
  window.location.reload();
}

/**
 * Register the warm-reopen version check (visibilitychange → visible). Called
 * once on mount for ALL users; the mid-exchange gate keeps Anon safe.
 */
export function startVersionCheck() {
  if (initialized) return;
  initialized = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      maybeReloadForUpdate();
    }
  });
}
